import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Copy,
  Download,
  FileText,
  RefreshCw,
  Sparkles,
  Loader2,
  Briefcase,
  Heart,
  Minimize2,
  Maximize2,
  Mail,
  ArrowLeft,
} from "lucide-react";
import { OUTPUT } from "@/constants/testIds";
import { SITE } from "@/config/site";
import ResultsCallout from "@/components/ResultsCallout";
import CoachingOptIn from "@/components/CoachingOptIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
  { key: "regenerate", label: "Regenerate", icon: RefreshCw, testid: OUTPUT.regenerateBtn },
  { key: "professional", label: "More Professional", icon: Briefcase, testid: OUTPUT.professionalBtn },
  { key: "warm", label: "Warmer & Community-Based", icon: Heart, testid: OUTPUT.warmBtn },
  { key: "shorten", label: "Shorten It", icon: Minimize2, testid: OUTPUT.shortenBtn },
  { key: "expand", label: "Expand It", icon: Maximize2, testid: OUTPUT.expandBtn },
  { key: "email", label: "Turn Into Email", icon: Mail, testid: OUTPUT.emailBtn },
];

function renderMarkdown(text) {
  const blocks = [];
  let buf = [];
  const flush = (i) => {
    if (buf.length) {
      blocks.push(
        <p key={`p-${i}-${blocks.length}`}>{buf.join(" ")}</p>
      );
      buf = [];
    }
  };
  text.split("\n").forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("## ")) {
      flush(i);
      blocks.push(<h2 key={`h-${i}`}>{trimmed.replace(/^##\s*/, "")}</h2>);
    } else if (trimmed.startsWith("# ")) {
      flush(i);
      blocks.push(<h2 key={`h-${i}`}>{trimmed.replace(/^#\s*/, "")}</h2>);
    } else if (!trimmed) {
      flush(i);
    } else {
      buf.push(trimmed);
    }
  });
  flush("end");
  return blocks;
}

export default function Output() {
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeAction, setActiveAction] = useState("");
  const [downloading, setDownloading] = useState("");
  const [view, setView] = useState("preview"); // preview | edit

  useEffect(() => {
    const raw = sessionStorage.getItem("fl_proposal");
    if (!raw) {
      navigate("/build");
      return;
    }
    const p = JSON.parse(raw);
    setProposal(p);
    setContent(p.content || "");
  }, [navigate]);

  const title = useMemo(() => {
    if (!proposal) return "Proposal";
    const org = proposal.form?.organization_name || "";
    const t = proposal.proposal_type || "Proposal";
    return org ? `${org} — ${t}` : t;
  }, [proposal]);

  const runTransform = async (action) => {
    if (!proposal) return;
    setBusy(true);
    setActiveAction(action);
    try {
      const { data } = await axios.post(`${API}/proposals/transform`, {
        content,
        action,
        form: action === "regenerate" ? proposal.form : undefined,
      });
      setContent(data.content);
      toast.success("Updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update draft.");
    } finally {
      setBusy(false);
      setActiveAction("");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const res = await axios.post(
        `${API}/proposals/download/${kind}`,
        { content, title },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/\s+/g, "_")}.${kind === "docx" ? "docx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading("");
    }
  };

  if (!proposal) return null;

  return (
    <div data-testid={OUTPUT.root} className="min-h-screen">
      <header className="nav-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-700 hover:text-slate-900"
          >
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-heading font-semibold">{SITE.brand}</span>
          </button>
          <Button
            variant="ghost"
            onClick={() => navigate("/build")}
            className="rounded-full"
            data-testid={OUTPUT.startOverBtn}
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Start over
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar tools */}
          <aside className="lg:col-span-3 space-y-6 order-2 lg:order-1">
            <div className="paper-surface p-5">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-400 mb-3">
                Document
              </div>
              <div className="font-heading text-base font-semibold text-slate-900 leading-snug">
                {title}
              </div>

              <div className="mt-5 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="rounded-full border-slate-200"
                  onClick={copy}
                  data-testid={OUTPUT.copyBtn}
                >
                  <Copy className="h-4 w-4 mr-1.5" /> Copy
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full border-slate-200"
                  onClick={() => download("docx")}
                  disabled={downloading === "docx"}
                  data-testid={OUTPUT.downloadDocxBtn}
                >
                  {downloading === "docx" ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-1.5" />
                  )}
                  Word
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full border-slate-200 col-span-2"
                  onClick={() => download("pdf")}
                  disabled={downloading === "pdf"}
                  data-testid={OUTPUT.downloadPdfBtn}
                >
                  {downloading === "pdf" ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1.5" />
                  )}
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="paper-surface p-5">
              <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-400 mb-3">
                Refine the draft
              </div>
              <div className="space-y-2">
                {ACTIONS.map((a) => (
                  <Button
                    key={a.key}
                    variant="ghost"
                    className="w-full justify-start rounded-xl hover:bg-teal-50 text-slate-700 whitespace-normal text-left h-auto py-2.5 leading-tight"
                    onClick={() => runTransform(a.key)}
                    disabled={busy}
                    data-testid={a.testid}
                  >
                    {busy && activeAction === a.key ? (
                      <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" />
                    ) : (
                      <a.icon className="h-4 w-4 mr-2 shrink-0 text-primary" />
                    )}
                    <span className="flex-1 min-w-0">{a.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          </aside>

          {/* Editor */}
          <main className="lg:col-span-9 order-1 lg:order-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">
                  Your draft
                </span>
                <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                  {proposal.proposal_type}
                </h1>
              </div>
              <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 text-xs font-medium">
                <button
                  onClick={() => setView("preview")}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    view === "preview" ? "bg-primary text-white" : "text-slate-600"
                  }`}
                  data-testid="output-view-preview"
                >
                  Preview
                </button>
                <button
                  onClick={() => setView("edit")}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    view === "edit" ? "bg-primary text-white" : "text-slate-600"
                  }`}
                  data-testid="output-view-edit"
                >
                  Edit
                </button>
              </div>
            </div>

            <div className="paper-surface p-6 sm:p-10 min-h-[600px]">
              {view === "preview" ? (
                <article className="markdown-preview">{renderMarkdown(content)}</article>
              ) : (
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full min-h-[560px] border-none focus-visible:ring-0 px-0 text-base leading-relaxed font-mono"
                  data-testid={OUTPUT.editor}
                />
              )}
            </div>

            <ResultsCallout />
            <CoachingOptIn
              proposalType={proposal.proposal_type}
              organizationName={proposal.form?.organization_name}
            />
          </main>
        </div>
      </div>
    </div>
  );
}
